import { WHITE_LABEL_ID } from "config";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import brandingService from "services/brandingService";

const BrandingContext = createContext();

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Carga la configuración de branding
   * @param {number|null} negocioId - ID del negocio (opcional, legacy)
   */
  const loadBranding = useCallback(async (negocioId = null) => {
    try {
      setLoading(true);
      setError(null);

      let brandingData;

      // Priorizar WHITE_LABEL_ID de variables de entorno
      if (WHITE_LABEL_ID) {
        console.log(`📋 Loading branding for WHITE_LABEL_ID: ${WHITE_LABEL_ID}`);
        brandingData = await brandingService.getBranding();
      } else if (negocioId) {
        console.log(`📋 Loading branding for negocio (legacy): ${negocioId}`);
        brandingData = await brandingService.getBranding(negocioId);
      } else {
        console.log("📋 Loading branding with automatic detection");
        brandingData = await brandingService.getBranding();
      }

      setBranding(brandingData);
      console.log("✅ Branding data set in context:", brandingData);

      // Aplicar el branding al DOM
      brandingService.applyBranding(brandingData);
    } catch (err) {
      console.error("Error loading branding:", err);
      setError(err);

      // Usar configuración por defecto en caso de error
      const defaultBranding = brandingService.getDefaultBranding();
      setBranding(defaultBranding);
      brandingService.applyBranding(defaultBranding);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresca la configuración de branding
   */
  const refreshBranding = useCallback(
    async (negocioId = null) => {
      // Priorizar WHITE_LABEL_ID sobre negocioId
      if (WHITE_LABEL_ID) {
        brandingService.clearCache();
        await loadBranding();
      } else {
        brandingService.clearCache();
        await loadBranding(negocioId);
      }
    },
    [loadBranding]
  );

  /**
   * Obtiene el nombre de la marca actual
   */
  const getBrandName = () => {
    // Manejar estructura {data: {...}} que viene del API
    const brandingData = branding?.data || branding;
    return brandingData?.white_label?.name || "Rhino";
  };

  /**
   * Verifica si es la marca Handy (compatibilidad hacia atrás)
   */
  const isHandyBrand = () => {
    return brandingService.isHandyBrand(branding);
  };

  /**
   * Obtiene la configuración de tema actual
   */
  const getThemeConfig = () => {
    return brandingService.getThemeConfig(branding);
  };

  /**
   * Obtiene el valor de un color específico del branding
   */
  const getBrandColor = (colorType) => {
    // Manejar estructura {data: {...}} que viene del API
    const brandingData = branding?.data || branding;
    const colors = brandingData?.branding;
    if (!colors) return null;

    switch (colorType) {
      case "primary":
        return colors.primary_color;
      case "secondary":
        return colors.secondary_color;
      default:
        return null;
    }
  };

  /**
   * Obtiene la URL de un recurso específico
   */
  const getBrandResource = (resourceType) => {
    // Manejar estructura {data: {...}} que viene del API
    const brandingData = branding?.data || branding;
    const resources = brandingData?.branding;
    if (!resources) return null;

    switch (resourceType) {
      case "logo":
        return resources.logo_url;
      case "favicon":
        return resources.favicon_url;
      default:
        return null;
    }
  };

  // Cargar branding cuando cambie el negocio
  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  // Escuchar cambios en el host para recargar branding
  useEffect(() => {
    const handleLocationChange = () => {
      loadBranding();
    };

    // Escuchar cambios de ruta que puedan indicar cambio de dominio
    window.addEventListener("popstate", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, [loadBranding]);

  const contextValue = {
    branding,
    loading,
    error,
    loadBranding,
    refreshBranding,
    getBrandName,
    isHandyBrand,
    getThemeConfig,
    getBrandColor,
    getBrandResource,

    // Métodos de compatibilidad para facilitar la migración
    getCurrentBrandConfig: () => branding,
    getBrandUrl: () => branding?.white_label?.url || "https://rhinox.io/",
    getBrandSiteName: () => branding?.white_label?.siteName || "rhinox.io",
  };

  return <BrandingContext.Provider value={contextValue}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};

export default BrandingContext;
